import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import type {
  Report,
  ReportDocument,
  ReportFavorite,
  ReportDocumentParams,
  ReportDocumentStatus,
  ReportResult,
  QueueReportRequest,
  ScheduledReport,
  ScheduledReportLog,
  ScheduledReportResult,
  FacilitySet,
  RecipeItem,
} from '../types/reports';

// ===== QUERY KEYS =====
export const reportKeys = {
  all: ['reports'] as const,
  list: (facilityId: number) => [...reportKeys.all, 'list', facilityId] as const,
  documents: (facilityId: number) => [...reportKeys.all, 'documents', facilityId] as const,
  favorites: (facilityId: number, userId: number) =>
    [...reportKeys.all, 'favorites', facilityId, userId] as const,
  schedules: (facilityId: number) => [...reportKeys.all, 'schedules', facilityId] as const,
  scheduleHistory: (facilityId: number, scheduleId: number) =>
    [...reportKeys.all, 'scheduleHistory', facilityId, scheduleId] as const,
};

// ===== REPORT LIST =====

export function useReportList(facilityId: number) {
  return useQuery({
    queryKey: reportKeys.list(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<Report[]>(API.reportList(facilityId));
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

// ===== FAVORITES =====

export function useReportFavorites(facilityId: number, userId: number) {
  return useQuery({
    queryKey: reportKeys.favorites(facilityId, userId),
    queryFn: async () => {
      const { data } = await apiClient.get<ReportFavorite[]>(
        API.reportFavorites(facilityId, userId),
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0 && userId > 0,
  });
}

export function useAddFavorite(facilityId: number, userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (favorite: Omit<ReportFavorite, 'pKey_PM_User_ReportFavorite'>) => {
      const { data } = await apiClient.post<number>(
        API.reportFavoritesAdd(facilityId),
        favorite,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.favorites(facilityId, userId) });
    },
  });
}

export function useDeleteFavorite(facilityId: number, userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (favoriteId: number) => {
      await apiClient.delete(API.reportFavoritesDelete(facilityId, favoriteId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.favorites(facilityId, userId) });
    },
  });
}

// ===== REPORT DOCUMENTS (PRINT QUEUE) =====

/** Imperative fetch for print queue - allows manual refresh control */
export async function fetchReportDocuments(
  facilityId: number,
  params: ReportDocumentParams,
): Promise<ReportDocument[]> {
  const { data } = await apiClient.post<ReportDocument[]>(
    API.reportDocuments(facilityId),
    params,
  );
  return data ?? [];
}

/**
 * Status of specific report documents (POST api/report/Status). What the
 * auto-opener polls while a report it queued renders: one row per document,
 * instead of the user's whole print queue.
 */
export async function fetchReportStatuses(
  facilityId: number,
  reportDocumentIds: number[],
): Promise<ReportDocumentStatus[]> {
  const { data } = await apiClient.post<ReportDocumentStatus[]>(
    API.reportStatus(facilityId),
    { reportDocumentIds },
  );
  return data ?? [];
}

export function useDeleteDocument(facilityId: number) {
  return useMutation({
    mutationFn: async ({ reportDocumentId, userId }: { reportDocumentId: number; userId: number }) => {
      await apiClient.delete(API.reportDeleteDocument(facilityId, reportDocumentId), {
        params: { userId },
      });
    },
  });
}

export function useReprocessDocument(facilityId: number) {
  return useMutation({
    mutationFn: async ({ reportDocumentId, userId }: { reportDocumentId: number; userId: number }): Promise<number> => {
      // API returns the newly-cloned PKey_RS_ReportDocument (0 on miss);
      // caller uses it to focus the new row in the grid after refresh.
      const res = await apiClient.post<number>(API.reportReprocess(facilityId, reportDocumentId), null, {
        params: { userId },
      });
      return res.data ?? 0;
    },
  });
}

export function useCancelDocument(facilityId: number) {
  return useMutation({
    mutationFn: async ({ reportDocumentId, userId }: { reportDocumentId: number; userId: number }) => {
      await apiClient.post(API.reportCancel(facilityId, reportDocumentId), null, {
        params: { userId },
      });
    },
  });
}

export function useMarkAsPrinted(facilityId: number) {
  return useMutation({
    mutationFn: async ({ reportDocumentId, userId }: { reportDocumentId: number; userId: number }) => {
      await apiClient.post(API.reportMarkPrinted(facilityId, reportDocumentId), null, {
        params: { userId },
      });
    },
  });
}

// ===== REPORT PDF URL =====

export function getReportPdfUrl(facilityId: number, guid: string): string {
  return API.reportPdf(facilityId, guid);
}

/**
 * Fetch a completed report's PDF through the authenticated apiClient — which
 * adds the API base URL, the JWT, and X-Connection-Id. Returns the PDF blob.
 *
 * Do NOT build a raw URL and window.open() it: relative "/api/..." resolves
 * against the static site origin (dietechonline.com → 404), and a bare new-tab
 * GET carries no auth header or X-Connection-Id, so the API can't authorize or
 * resolve the tenant DB. Fetch the blob here and open the object URL instead.
 */
export async function getReportPdfBlob(facilityId: number, guid: string): Promise<Blob> {
  const response = await apiClient.get<Blob>(
    API.reportPdf(facilityId, guid),
    { responseType: 'blob' },
  );
  return response.data;
}

/** Formats GET /api/report/Export accepts (ReportExportFormats on the API). */
export type ReportExportFormat = 'xlsx' | 'docx' | 'csv';

/**
 * The stored report exported to Excel, Word or CSV by the API (DevExpress
 * PrintingSystem.ExportToXlsx / ExportToDocx), for the report viewer's Export menu.
 */
export async function getReportExportBlob(facilityId: number, guid: string, format: ReportExportFormat): Promise<Blob> {
  const response = await apiClient.get<Blob>(
    API.reportExport(facilityId, guid),
    { params: { format }, responseType: 'blob' },
  );
  return response.data;
}

/**
 * Server-side merge of 2+ completed report documents into one PDF.
 * The API also stamps each contributing document as Printed. Returns
 * the merged bytes for browser download.
 */
export async function combineReportsPdf(
  facilityId: number,
  reportDocumentIds: number[],
  userId: number,
): Promise<Blob> {
  const response = await apiClient.post<Blob>(
    API.reportCombinePdf(facilityId),
    { reportDocumentIds, userId },
    { responseType: 'blob' },
  );
  return response.data;
}

// ===== REPORT DOCUMENT BYTES (direct download) =====

export function getReportDocumentBytesUrl(facilityId: number, guid: string): string {
  return API.reportDocumentBytes(facilityId, guid);
}

// ===== COST ANALYSIS REPORTS =====

export function useQueueCostMenuReport(facilityId: number) {
  return useMutation({
    mutationFn: async (settings?: Record<string, unknown>) => {
      const { data } = await apiClient.post<ReportResult>(
        API.reportQueueCostMenu(facilityId),
        settings ?? {},
      );
      return data;
    },
  });
}

export function useQueueCostNourishmentReport(facilityId: number) {
  return useMutation({
    mutationFn: async (settings?: Record<string, unknown>) => {
      const { data } = await apiClient.post<ReportResult>(
        API.reportQueueCostNourishment(facilityId),
        settings ?? {},
      );
      return data;
    },
  });
}

// ===== QUEUE REPORTS =====

/** Queue requests on their way, by report: see queueReportOnce. */
const queueing = new Map<string, Promise<ReportResult>>();

/** The same report, asked for by the same user at the same facility. */
function queueKey(facilityId: number, request: QueueReportRequest): string {
  return [facilityId, request.fKey_User, request.fKey_RS_Report, request.reportType, request.reportName].join('|');
}

/**
 * POST report/Queue, saying so the moment it is asked for. A tester, 11-03: "Delay from clicking
 * generate to the popup, so I had to do it again; the first click didn't seem to take." Every toast
 * about a queued report (the viewer's "Generating", "queued, it appears in the Print Queue") waited
 * for the queue to answer, and a second click queued the report twice.
 *
 * Now a "Queuing" toast shows at once and stays until the queue answers, when the caller's own toast
 * takes over. A request for the same report while one is on its way is not sent again: it gets the
 * first one's answer. When the queue fails, the toast goes, the caller says so, and the next click
 * queues it.
 */
export function queueReportOnce(facilityId: number, request: QueueReportRequest): Promise<ReportResult> {
  const key = queueKey(facilityId, request);
  const onItsWay = queueing.get(key);
  if (onItsWay) return onItsWay;
  const toastId = useNotificationStore.getState().addNotification({
    message: `Queuing "${request.reportName || 'the report'}"...`,
    type: 'info',
    duration: 0, // until the queue answers
    showSpinner: true,
  });
  const sent = apiClient.post<ReportResult>(API.reportQueue(facilityId), request)
    .then(({ data }) => data)
    .finally(() => {
      queueing.delete(key);
      if (toastId) useNotificationStore.getState().dismissNotification(toastId);
    });
  queueing.set(key, sent);
  return sent;
}

/**
 * Queue a report (POST report/Queue, through queueReportOnce). The result is the caller's to show: the
 * queue answers HTTP 200 with `success: false` when it could not queue (for example a report type with no
 * tblRS_Report row), so every caller must show `message` as an error when `success` is false
 * (reportQueueFailureToasts.test.ts checks each caller). reportApi's useQueueReport shows its own toasts
 * instead.
 */
export function useQueueReport(facilityId: number) {
  return useMutation({
    mutationFn: (request: QueueReportRequest) => queueReportOnce(facilityId, request),
  });
}

export function useQueueSpecificReport(facilityId: number) {
  return useMutation({
    mutationFn: async ({
      reportType,
      settings,
    }: {
      reportType: string;
      settings: Record<string, unknown>;
    }) => {
      const { data } = await apiClient.post<ReportResult>(
        API.reportQueueSpecific(facilityId, reportType),
        settings,
      );
      return data;
    },
  });
}

// ===== TRAY TICKET IMAGE PICKER =====

/** Row shape returned by `GET /api/TrayTicketImages/List/{facilityId}`.
 *  Mirrors the WinForms picker: `ResourceName` is what gets sent on
 *  the wire (`ImageName`); `Group` becomes the group-row header;
 *  `Name` is the display label; `ImageUrl` is a per-row PNG URL for
 *  the thumbnail preview. */
export interface TrayTicketImageRow {
  resourceName: string;
  group: string;
  name: string;
  imageUrl: string;
}

/** One row in the Non-Returned Selective Menu pre-print dialog.
 *  Server DTO camelCased by System.Text.Json. */
export interface TrayTicketNonReturnedSelectiveMenuRow {
  pKey_Patient: number;
  fKey_Patient: number | null;
  fullName: string;
  sortName: string;
  mealtimeInfo: string;
  mealtime: number;
  menuDate: string;
  staticSelectiveMenu: boolean;
  print: boolean;
}

/** Imperative fetch — the caller (tray-ticket submit path) already
 *  has all the request parameters and shouldn't cache; if the user
 *  tweaks any filter the picker must re-run against fresh selective
 *  menu state. */
export async function fetchTrayTicketNonReturnedSelectiveMenus(
  facilityId: number,
  request: {
    startDate: string;
    numberOfDays: number;
    pKey_Menu_Header: number;
    uniqueMealtimes: number[];
    setList?: number[] | null;
    patientList?: number[] | null;
  },
): Promise<TrayTicketNonReturnedSelectiveMenuRow[]> {
  const { data } = await apiClient.post<{ rows: TrayTicketNonReturnedSelectiveMenuRow[] }>(
    API.trayTicketNonReturnedSelectiveMenus(facilityId),
    {
      StartDate: request.startDate,
      NumberOfDays: request.numberOfDays,
      PKey_Menu_Header: request.pKey_Menu_Header,
      UniqueMealtimes: request.uniqueMealtimes,
      SetList: request.setList ?? null,
      PatientList: request.patientList ?? null,
    },
  );
  return data?.rows ?? [];
}

/** Preset Tray Ticket note strings for a facility. Mirrors WinForms
 *  MRUEdit fed by `tblFacility.TT_Notes`. Cached per facility;
 *  short staleTime because a user in facility admin can update the
 *  list without reloading the app. */
export function useTrayTicketNotes(facilityId: number) {
  return useQuery<string[]>({
    queryKey: ['trayTicketNotes', facilityId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<string[]>(
        API.trayTicketNotes(facilityId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/** Fetch the tray-ticket image picker rows for a facility. The list
 *  is stable per facility (facility-scoped Resident-Image entry
 *  aside) so we cache aggressively. */
export function useTrayTicketImages(facilityId: number) {
  return useQuery<TrayTicketImageRow[]>({
    queryKey: ['trayTicketImages', facilityId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<TrayTicketImageRow[]>(
        API.trayTicketImagesList(facilityId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

// ===== SCHEDULED REPORTS =====

/** The signed-in user's schedules; with `allUsers` everyone's (Report_Level 3, enforced by the API). */
export function useScheduledReports(facilityId: number, includeInactive: boolean, allUsers = false) {
  return useQuery({
    queryKey: [...reportKeys.schedules(facilityId), includeInactive, allUsers],
    queryFn: async () => {
      const { data } = await apiClient.get<ScheduledReport[]>(
        API.reportSchedules(facilityId),
        { params: { includeInactive, allUsers } },
      );
      return data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

/**
 * Whether the API runs scheduled reports (ScheduledReports:Enabled). `data` is
 * undefined until it answers, and stays undefined if the check fails, so the
 * Scheduled tab only speaks up when the API says the runner is off.
 */
export function useScheduleRunnerStatus(facilityId: number) {
  return useQuery({
    queryKey: [...reportKeys.all, 'scheduleRunner', facilityId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<{ enabled: boolean }>(API.reportScheduleRunner(facilityId));
      return data;
    },
    staleTime: 10 * 60 * 1000,
    retry: false,
    enabled: facilityId > 0,
  });
}

export function useScheduleHistory(
  facilityId: number,
  scheduleId: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: reportKeys.scheduleHistory(facilityId, scheduleId),
    queryFn: async () => {
      const { data } = await apiClient.get<ScheduledReportLog[]>(
        API.reportScheduleHistory(facilityId, scheduleId),
      );
      return data;
    },
    staleTime: 30 * 1000,
    enabled: enabled && facilityId > 0 && scheduleId > 0,
  });
}

export function useCreateSchedule(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (schedule: Partial<ScheduledReport>) => {
      const { data } = await apiClient.post<ScheduledReportResult>(
        API.reportSchedules(facilityId),
        schedule,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.schedules(facilityId) });
    },
  });
}

export function useUpdateSchedule(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (schedule: ScheduledReport) => {
      const { data } = await apiClient.put<ScheduledReportResult>(
        API.reportScheduleById(facilityId, schedule.pKey_RS_ScheduledReport),
        schedule,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.schedules(facilityId) });
    },
  });
}

export function useDeleteSchedule(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleId: number) => {
      const { data } = await apiClient.delete<ScheduledReportResult>(
        API.reportScheduleById(facilityId, scheduleId),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.schedules(facilityId) });
    },
  });
}

export function useToggleSchedule(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      scheduleId,
      isActive,
    }: {
      scheduleId: number;
      isActive: boolean;
    }) => {
      // The API reads isActive from the query string (it was sent in the body and always read false).
      const { data } = await apiClient.patch<ScheduledReportResult>(
        API.reportScheduleToggle(facilityId, scheduleId),
        null,
        { params: { isActive } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportKeys.schedules(facilityId) });
    },
  });
}

export function useRunScheduleNow(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleId: number) => {
      const { data } = await apiClient.post<ReportResult>(
        API.reportScheduleRun(facilityId, scheduleId),
      );
      return data;
    },
    onSuccess: (_d, scheduleId) => {
      queryClient.invalidateQueries({ queryKey: reportKeys.schedules(facilityId) });
      queryClient.invalidateQueries({ queryKey: reportKeys.scheduleHistory(facilityId, scheduleId) });
    },
  });
}

// ===== FACILITY SETS (for report selectors) =====

export function useFacilitySets(facilityId: number) {
  return useQuery({
    queryKey: [...reportKeys.all, 'facilitySets', facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<FacilitySet[]>(
        API.facilitySets(facilityId),
      );
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

/** Active residents per {set} number on `date` (yyyy-MM-dd). Null `date` = not asked for. */
export function useSetResidentCounts(facilityId: number, date: string | null) {
  return useQuery({
    queryKey: [...reportKeys.all, 'setResidentCounts', facilityId, date],
    queryFn: async () => {
      const { data } = await apiClient.get<{ number: number; residents: number }[]>(
        API.setResidentCounts(facilityId, date as string),
        { _skipVersionMismatch: true } as never,
      );
      return new Map((data ?? []).map((c) => [c.number, c.residents]));
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0 && !!date,
    // An API slot without the endpoint (404) just shows no counts.
    retry: false,
  });
}

// ===== RECIPES (for report selectors) =====

export function useRecipes(facilityId: number) {
  return useQuery({
    queryKey: [...reportKeys.all, 'recipes', facilityId],
    queryFn: async () => {
      const { data } = await apiClient.get<RecipeItem[]>(
        API.recipes(facilityId),
      );
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: facilityId > 0,
  });
}
